import {
  MinusCircleOutlined,
  PartitionOutlined,
  PlusCircleOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import type { ViewConfigTreeNode } from '@salescore/core'
import { disableOnClickOutsideProperties } from '@salescore/frontend-common'
import { Button, Input, Row, Table } from 'antd'
import { Array, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { viewsContextAtom } from '../../../../recoil/view/atoms'
import { useGetModel } from '../../../../recoil/view/selectors/connectionsSelector'
import type { ReferenceToPropertyForAddChildNodeMutation } from '../findSameReferenceToChild'
import { TABLE_HEIGHT } from './const'

export function isSystemModel(modelName: string): boolean {
  return [
    `salescore_users`,
    `salescore_user_groups`,
    `salescore_kpis`,
    `salescore_goals`,
    `salescore_record_changes`,
    `salescore_my_actions`,
    // Reverse ELT 機能は #6965 で廃止したが、2023 年の一回限り migration
    // (cli/migrations/old/20230303_create_reverse_elt_logs_table.ts) が一部組織に
    // elt_model 行を直接 upsert しており、refreshEltModelsService は salescore provider を
    // 早期 return するためこの行は削除経路に乗らない。行が残っている組織では、本行を外すと
    // 実体のないモデルがプロパティセレクタに出る。行の有無を調査して削除する #9860 で
    // 本行も削除する
    `salescore_reverse_elt_logs`,
  ].includes(modelName)
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function PropertySelectorTableBodyForParentChildrenNode({
  node,
  addChildNode,
}: {
  node: ViewConfigTreeNode
  addChildNode: ({ referenceToProperty }: { referenceToProperty: ReferenceToPropertyForAddChildNodeMutation }) => void
}) {
  const { name: nodeName, modelName } = node
  const { models } = useRecoilValue(viewsContextAtom)
  const getModel = useGetModel()
  const [searchKey, setSearchKey] = useState('')

  const { referenceToProperties } = useMemo(() => {
    const currentModel = getModel(modelName)
    if (currentModel === undefined) {
      return {
        referenceToProperties: [],
      }
    }

    //
    // 子ノードの候補を作成
    //
    // このノードのモデルの参照プロパティ
    const parentRelationNodes = currentModel.properties.flatMap((property) => {
      const referenceTo = property.referenceTo ?? []
      if (referenceTo.length === 0) {
        return []
      }

      return Array.flatMapNullishOr(referenceTo, (referenceToValue) => {
        const referencedModel = getModel(referenceToValue.modelName)
        if (referencedModel === undefined) {
          return
        }

        const referenceToProperty: ReferenceToPropertyForAddChildNodeMutation = {
          parentNodeName: nodeName,
          nodeName,
          modelName: currentModel.name,
          propertyName: property.name,
          referenceTo: referenceToValue,
        }
        return {
          type: 'parent',
          newNodeModel: referencedModel,
          property,
          description: t(`{{label1}}と{{label2}}で紐付け`, {
            label1: `${referencedModel.label}.${referenceToValue.key ?? 'id'}`,
            label2: `${currentModel.label}.${property.label}`,
          }),
          referenceToProperty,
        }
      })
    })

    // このノードのモデルを参照している、他のモデルの参照プロパティ
    const childRelationNodes = pipe(
      models,
      Array.filter((x) => !isSystemModel(x.name)),
      Array.flatMap((model) =>
        // 2023/02/13 同じモデルでも表示させることにした。元々なぜこれを廃止していたか謎。ブロックの表示が微妙になるのは別途解消する
        // if (model.name === currentModel.name) {
        //   return []
        // }
        model.properties.map((property) => {
          const referenceTo = property.referenceTo ?? []
          // eslint-disable-next-line max-nested-callbacks
          const referenceToValue = referenceTo.find((x) => x.modelName === currentModel.name)
          if (referenceToValue === undefined) {
            // eslint-disable-next-line array-callback-return
            return
          }

          const referenceToProperty: ReferenceToPropertyForAddChildNodeMutation = {
            parentNodeName: nodeName,
            nodeName: undefined,
            modelName: model.name,
            propertyName: property.name,
            referenceTo: referenceToValue,
          }

          return {
            type: 'child',
            newNodeModel: model,
            property,
            description: t(`{{label1}}と{{label2}}で紐づけ`, {
              label1: `${model.label}.${property.label}`,
              label2: `${currentModel.label}.${referenceToValue.key ?? 'id'}`,
            }),
            referenceToProperty,
          }
        }),
      ),
      Array.filter(isNotNullish),
    )

    return {
      referenceToProperties: [...parentRelationNodes, ...childRelationNodes],
    }
  }, [getModel, modelName, models, nodeName])

  // 大文字小文字を区別せず部分一致で絞り込む
  const lowerSearchKey = searchKey.toLowerCase()
  const nodeGroups = pipe(
    referenceToProperties,
    Array.groupBy((x) => [x.newNodeModel.name, x.type].join(',')),
    Record.values,
    Array.map((rProperties) => {
      const [{ newNodeModel }] = rProperties
      return {
        key: [newNodeModel.name, rProperties[0].type].join(`_`),
        newNodeModel,
        type: rProperties[0].type,
        referenceToProperties: rProperties,
      }
    }),
    // オブジェクト名(label)・内部名(name)の部分一致で候補を絞り込む
    Array.filter(
      ({ newNodeModel }) =>
        newNodeModel.label.toLowerCase().includes(lowerSearchKey) ||
        newNodeModel.name.toLowerCase().includes(lowerSearchKey),
    ),
  )

  const [expandedRowKey, setExpandedRowKey] = useState<string | undefined>()
  return (
    <>
      <Row className="my-3" align="middle">
        <Input.Search
          autoFocus // 初期表示で検索ボックスにフォーカスする
          allowClear
          enterButton={false}
          className="w-full"
          placeholder={t('検索')}
          onChange={(event) => {
            setSearchKey(event.target.value)
          }}
        />
      </Row>
      <Table
        scroll={{ y: TABLE_HEIGHT - 70 }}
        dataSource={nodeGroups}
        pagination={false}
        size="small"
        rowClassName="cursor-pointer hover:opacity-80"
        expandable={{
          columnWidth: 24,
          rowExpandable(nodeGroup) {
            return nodeGroup.referenceToProperties.length > 1
          },
          expandedRowKeys: [expandedRowKey].filter(isNotNullish),
          expandRowByClick: true,
          expandIcon(properties) {
            if (!properties.expandable) {
              return <></>
            }
            if (properties.expanded) {
              return (
                <MinusCircleOutlined
                  onClick={() => {
                    setExpandedRowKey(undefined)
                  }}
                />
              )
            }
            return (
              <PlusCircleOutlined
                onClick={() => {
                  setExpandedRowKey(properties.record.key)
                }}
              />
            )
          },
          expandedRowRender(nodeGroup) {
            if (nodeGroup.referenceToProperties.length <= 1) {
              return
            }
            return (
              <Table
                dataSource={nodeGroup.referenceToProperties}
                pagination={false}
                showHeader={false}
                onRow={({ referenceToProperty }) => ({
                  onClick() {
                    addChildNode({ referenceToProperty })
                  },
                  // className: isIncluded(node, viewQueryFields) ? 'bg-blue-50' : '',
                })}
                columns={[
                  {
                    key: 'description',
                    render(_value, node) {
                      return <span className="text-sm">{node.description}</span>
                    },
                  },
                  {
                    width: 40,
                    render(_, record) {
                      return (
                        <span>
                          <Button
                            // type="text"
                            size="small"
                            icon={<RightOutlined />}
                            {...disableOnClickOutsideProperties}
                          />
                        </span>
                      )
                    },
                  },
                ]}
              />
            )
          },
        }}
        onRow={(nodeGroup) => ({
          onClick() {
            if (nodeGroup.referenceToProperties.length > 1) {
              setExpandedRowKey((oldKey) => (nodeGroup.key === oldKey ? undefined : nodeGroup.key))
              return
            }
            setExpandedRowKey(undefined)

            const [{ referenceToProperty }] = nodeGroup.referenceToProperties
            addChildNode({ referenceToProperty })
          },
          // className: isIncluded(record, viewQueryFields) ? 'bg-blue-50' : '',
        })}
        columns={[
          {
            title: '',
            key: 'relationFromParentNode',
            width: 28,
            render(_, nodeGroup) {
              if (nodeGroup.type === 'parent') {
                return <SearchOutlined />
              }
              return <PartitionOutlined />
              // return <ClusterOutlined />
            },
          },
          {
            dataIndex: 'propertyLabel',
            title: t(`オブジェクト名`),
            render(_, nodeGroup) {
              return (
                <div>
                  <span>{nodeGroup.newNodeModel.label}</span>
                  {nodeGroup.referenceToProperties.length === 1 && (
                    <span className="ml-2 text-gray-400" style={{ fontSize: 10 }}>
                      {nodeGroup.referenceToProperties[0].description}
                    </span>
                  )}
                </div>
              )
            },
          },
          {
            width: 40,
            render(_, nodeGroup) {
              if (nodeGroup.referenceToProperties.length === 1) {
                return (
                  <span>
                    <Button
                      // type="text"
                      size="small"
                      icon={<RightOutlined />}
                      {...disableOnClickOutsideProperties}
                    />
                  </span>
                )
              }
              return <></>
            },
          },
        ]}
      />
    </>
  )
}
